import { useStore } from '@/containers'
import { IconClose, SearchIcon } from '@/styles/Icon'
import type { District, Province } from '@/types'
import { nonAccentVietnamese, removeAreaPrefix } from '@/utils/converter'
import { Dialog, Listbox, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { setCookie } from 'cookies-next'
import { Fragment, useEffect, useState, type ChangeEvent } from 'react'

export interface FormAddressProps {
  value: string
  name: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  className?: string
  type?: string
  classSelectButtonName?: string
  placeholder: string
  placeholderSearch: string
  isError?: boolean | string
  isICon?: boolean
  zIndex?: string
  defaultTextColor?: string
  onDropdownOpen?: () => void
  title: string
  data: (Province | District)[]
  disable?: boolean
}

export function FormAddress({
  placeholder,
  title,
  className,
  type,
  name,
  onChange,
  defaultTextColor = '',
  value,
  placeholderSearch = '',
  isError = false,
  classSelectButtonName = '',
  isICon = true,
  onDropdownOpen,
  data,
  disable = false,
}: FormAddressProps) {
  const [selected, setSelected] = useState<
    Province | District | null | undefined
  >()
  const {
    setRegionChange,
    setIsChangeRegionModal,
    currentRegion,
    getDistricts,
  } = useStore()
  const [searchTerm, setSearchTerm] = useState('')
  const filteredOptions = searchTerm
    ? data.filter(item =>
        nonAccentVietnamese(item.name).includes(nonAccentVietnamese(searchTerm))
      )
    : data

  const formatName = (name: string) => removeAreaPrefix(name)
  const onListBoxChange = async (listBoxValue: Province | null | undefined) => {
    if (disable) return
    onChange({
      target: { value: listBoxValue?.code, name },
    } as ChangeEvent<HTMLInputElement>)
    setSelected(listBoxValue)

    if (
      name == 'provinceCode' &&
      listBoxValue?.code &&
      listBoxValue?.code != currentRegion &&
      type == 'cart'
    ) {
      getDistricts(listBoxValue?.code)
      setIsChangeRegionModal(true)
      localStorage.setItem('province', listBoxValue?.code)
      setRegionChange(listBoxValue?.code)
      setCookie('province', listBoxValue?.code)
      await setCookie('Area-Code', listBoxValue?.code)
    }
    setSearchTerm('')
  }
  useEffect(() => {
    if (value === '') {
      setSelected(null)
      return
    }
    if (value && Array.isArray(data) && data.length > 0) {
      const normalizedValue = String(value)
      const valueDefault = data.find(
        item => String(item.code) === normalizedValue
      )
      if (valueDefault) {
        setSelected(valueDefault)
      }
      // If not found, keep previous selected to avoid flicker
    }
    // When data is temporarily empty, do not clear selected; wait for data to load
  }, [value, data])

  return (
    <div className={`relative ${className}`}>
      <Listbox
        value={selected || ''}
        onChange={onListBoxChange}
        name={name}
        disabled={disable}
      >
        {({ open }) => (
          <>
            <Listbox.Button
              onClick={() => (onDropdownOpen ? onDropdownOpen() : null)}
              className={`flex w-full items-center justify-start border text-sm ${
                isError ? 'border-states-error focus:ring-gray-light' : ''
              } focus:ring-gray-medium h-12 rounded-lg pl-3 ${classSelectButtonName} ${
                !isICon ? 'pr-3' : null
              }${disable ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <div className={`truncate ${selected ? defaultTextColor : ''}`}>
                {selected ? (
                  formatName(selected?.name)
                ) : (
                  <span className='text-dark-900'>{placeholder}</span>
                )}
              </div>
              {isICon && (
                <div className={`ml-auto px-3`}>
                  {open ? (
                    <IconDown className='rotate-180' />
                  ) : (
                    <IconUp className='' />
                  )}
                </div>
              )}
              <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'></span>
            </Listbox.Button>
            <div>
              <Transition appear show={open} as={Fragment}>
                <Dialog
                  as='div'
                  className={`relative z-[9999]`}
                  onClose={() => null}
                >
                  <Transition.Child
                    as={Fragment}
                    enter='transition-opacity ease-linear duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='transition-opacity ease-linear duration-300'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  >
                    <div className='fixed inset-0 bottom-0 left-0 z-[999] bg-black-150 transition-opacity' />
                  </Transition.Child>
                  <div className='pointer-events-none fixed bottom-0 left-[50%] z-[999] flex h-[55vh] max-h-[55vh] w-full max-w-[440px] translate-x-[-50%] overflow-hidden rounded-tl-lg rounded-tr-lg '>
                    <Transition.Child
                      as={Fragment}
                      enter='transition ease-in-out duration-700 transform'
                      enterFrom='translate-y-full'
                      enterTo='translate-y-0'
                      leave='transition ease-in-out duration-700 transform'
                      leaveFrom='translate-y-0'
                      leaveTo='translate-y-full'
                    >
                      <Dialog.Panel className='pointer-events-auto h-[55vh] w-full max-w-[440px]'>
                        <div
                          className={`block h-full w-full overflow-hidden bg-white`}
                        >
                          <div className='headerModal relative h-[60px] w-full px-2 pb-3 pt-4'>
                            <Dialog.Title
                              as='h2'
                              className={`text-titleSection`}
                            >
                              {title}
                            </Dialog.Title>
                            <button
                              onClick={onDropdownOpen}
                              className='absolute right-3 top-4 z-10 cursor-pointer'
                            >
                              <IconClose />
                            </button>
                          </div>
                          <div className={`w-full px-1`}>
                            <Listbox.Options
                              static
                              className='overflow-auto bg-white text-sm transition focus:outline-none'
                            >
                              <div className='relative bg-white-400'>
                                <div className='relative z-20 w-full bg-white-400 px-2'>
                                  <div className='absolute left-4 top-[50%] flex translate-y-[-50%]'>
                                    <SearchIcon />
                                  </div>
                                  <div className='block items-center'>
                                    <input
                                      type='search'
                                      name='searchAddress'
                                      autoFocus={true}
                                      id='searchAddress'
                                      value={searchTerm}
                                      placeholder={placeholderSearch}
                                      onKeyDown={e => {
                                        if (e.keyCode === 32) {
                                          e.stopPropagation()
                                        }
                                      }}
                                      onChange={e =>
                                        setSearchTerm(e.target.value)
                                      }
                                      className='block h-10 w-full rounded-lg border border-white-500 bg-white-500 pl-12 pr-6 text-sm outline-none transition placeholder:text-sm focus:border-primary-second focus:outline-none'
                                    />
                                    <div className='absolute right-3 top-[50%] flex translate-y-[-50%] py-1.5 pr-1.5 '>
                                      {searchTerm && (
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          className='mr-1 h-4 w-4 font-bold text-primary-second'
                                          viewBox='0 0 20 20'
                                          fill='currentColor'
                                          onClick={() => {
                                            setSearchTerm('')
                                          }}
                                        >
                                          <path
                                            fillRule='evenodd'
                                            d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                                            clipRule='evenodd'
                                          />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <ul className='resultList scrollBarCS mr-1 mt-3 h-full max-h-[calc(55vh-112px)] min-h-[calc(55vh-112px)] content-start overflow-y-auto pb-6 pl-2 pr-1 transition'>
                                  {filteredOptions &&
                                    filteredOptions.length == 0 && (
                                      <p className='mt-2 text-center'>
                                        Không có dữ liệu
                                      </p>
                                    )}
                                  {filteredOptions &&
                                    filteredOptions.length > 0 &&
                                    filteredOptions.map(addItem => (
                                      <Listbox.Option
                                        key={addItem._id}
                                        className={({ active, selected }) =>
                                          clsx(
                                            active
                                              ? 'bg-gray-500'
                                              : 'text-dark-400',
                                            selected
                                              ? 'bg-gray-500'
                                              : 'text-dark-400',
                                            'relative cursor-pointer select-none border-b-[1px] border-white-500 py-2 pl-3 pr-9 text-center text-sm'
                                          )
                                        }
                                        value={addItem}
                                      >
                                        {({ selected }) => (
                                          <>
                                            <span
                                              className={clsx(
                                                selected
                                                  ? 'font-semibold'
                                                  : 'font-normal',
                                                'block truncate'
                                              )}
                                            >
                                              {formatName(addItem.name)}
                                            </span>
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                </ul>
                              </div>
                            </Listbox.Options>
                          </div>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </Dialog>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  )
}

export default FormAddress

interface IconUp {
  className?: string
}

function IconUp({ className = '' }: IconUp) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
interface IconDown {
  className?: string
  width?: number
  height?: number
}

function IconDown({ className = 'fill-black' }: IconDown) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
